import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { CHIPS_COLOR, Task } from "../../types/tasks";
import { User } from "../../types/users";

interface Props {
  task: Task;
  users: User[];
  onDragStart: (id: string) => void;
  onClick: (task: Task) => void;
}

function TasksKanbanCard({ task, users, onDragStart, onClick }: Props) {
  const assignee = users?.find((u) => u._id === task.assignedTo);
  const initials = assignee?.username?.slice(0, 2).toUpperCase() ?? "–";

  const dueDate = task.dueDate ? moment(task.dueDate as any) : null;
  const isOverdue = dueDate && dueDate.isBefore(moment()) && task.status !== "Completed";

  return (
    <Box
      draggable
      onDragStart={() => onDragStart(task._id)}
      onClick={() => onClick(task)}
      sx={{
        bgcolor: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "6px",
        p: 1.5,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.08)", borderColor: "#CBD5E1" },
        transition: "box-shadow 0.1s ease, border-color 0.1s ease",
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}
          >
            {task.name}
          </Typography>
          <Tooltip title={assignee?.username ?? "Unassigned"} placement="top">
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                bgcolor: "#0F172A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: "white", fontSize: "0.55rem", fontWeight: 700 }}>
                {initials}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}>
          {task.refNo}
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            label={task.urgency}
            color={CHIPS_COLOR[task.urgency] ?? "default"}
            size="small"
            sx={{ fontSize: "0.6rem", height: 18, borderRadius: "3px", textTransform: "capitalize" }}
          />
          {dueDate && (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.6rem", color: isOverdue ? "#EF4444" : "#64748B", fontWeight: isOverdue ? 600 : 400 }}
            >
              {dueDate.fromNow()}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default TasksKanbanCard;
